// src/contexts/ThemeContext.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline } from '@mui/material';
import {
  ThemeProvider as MuiThemeProvider,
  useTheme as useMuiTheme,
  type Theme as MuiTheme,
} from '@mui/material/styles';
import { getAppTheme } from '../shared-theme';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  isDark: boolean;
}

type CombinedTheme = MuiTheme &
  ThemeContextValue & {
    muiTheme: MuiTheme;
  };

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyTheme(theme: Theme) {
  // This is the critical line — it's what makes CSS respond
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
}

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // 1. Check explicit user preference in localStorage
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) return stored === 'true' ? 'dark' : 'light';

    // 2. Fall back to OS preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  });

  // Apply on mount and every change
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('darkMode', theme === 'dark' ? 'true' : 'false');
    localStorage.setItem('themePreferenceSet', 'true');
  }, [theme]);

  // Also listen for OS preference changes (only when user hasn't overridden)
  useEffect(() => {
    const hasOverride = localStorage.getItem('themePreferenceSet') === 'true';
    if (hasOverride) return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) =>
      setTheme(e.matches ? 'dark' : 'light');

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleTheme = () =>
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));

  const muiTheme = useMemo(() => getAppTheme(theme), [theme]);
  const contextValue = useMemo(
    () => ({
      theme,
      toggleTheme,
      isDark: theme === 'dark',
    }),
    [theme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  const muiTheme = useMuiTheme();

  return Object.assign({}, muiTheme, ctx, {
    muiTheme,
  }) as CombinedTheme;
};
